import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, LockClosedIcon, ArrowRightIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter ao menos 6 caracteres'),
});

type LoginForm = z.infer<typeof loginSchema>;

// Partículas determinísticas (sem aleatoriedade para evitar re-render)
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: `${((i * 17 + 7) % 97) + 1}%`,
  bottom: `${(i * 13) % 30}px`,
  delay: `${(i * 0.6) % 9}s`,
  duration: `${7 + (i % 5)}s`,
  size: i % 4 === 0 ? 3 : i % 3 === 0 ? 2 : 1.5,
  drift: `${(i % 2 === 0 ? 1 : -1) * (8 + (i % 18))}px`,
}));

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [glowPulse, setGlowPulse]       = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // Iniciar a pulsação do glow após mount
  useEffect(() => {
    const t = setTimeout(() => setGlowPulse(true), 600);
    return () => clearTimeout(t);
  }, []);

  const handleLogin = async (data: LoginForm) => {
    try {
      await login(data.email, data.password);
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      const msg = Array.isArray(raw) ? raw.join(', ') : (raw ?? 'E-mail ou senha inválidos');
      toast.error(msg);
    }
  };

  const busy = isLoading || isSubmitting;

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: '#0a0f1e' }}
    >
      {/* ── Grid pattern ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,212,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,212,255,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridScroll 20s linear infinite',
        }}
      />

      {/* ── Glow orbs de fundo ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-15%', right: '-10%',
          width: '55vw', height: '55vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)',
          animation: 'orb1 12s ease-in-out infinite',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '-10%', left: '-8%',
          width: '45vw', height: '45vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,100,180,0.08) 0%, transparent 70%)',
          animation: 'orb2 15s ease-in-out infinite',
        }}
      />

      {/* ── Partículas flutuantes ── */}
      {PARTICLES.map((p) => (
        <div
          key={p.id}
          className="absolute pointer-events-none rounded-full"
          style={{
            left: p.left,
            bottom: p.bottom,
            width: p.size,
            height: p.size,
            background: '#00d4ff',
            opacity: 0,
            animation: `particleUp ${p.duration} ${p.delay} infinite linear`,
            // @ts-expect-error -- CSS custom property not in CSSProperties
            '--drift': p.drift,
          }}
        />
      ))}

      {/* ── Watermark ── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <img
          src="/img/inoveitech_whatsapp.svg"
          alt=""
          draggable={false}
          style={{ width: 'min(65vw, 560px)', opacity: 0.035, userSelect: 'none' }}
        />
      </div>

      {/* ── Conteúdo principal ── */}
      <div className="relative z-10 w-full max-w-sm px-4 py-8" style={{ animation: 'fadeSlideUp 0.6s ease-out both' }}>

        {/* Logo banner */}
        <div style={{ animation: 'logoFloat 6s ease-in-out infinite', marginBottom: '2px' }}>
          <img
            src="/img/inoveitech_logo.svg"
            alt="IncluirTech"
            className="w-full"
            style={{ borderRadius: '12px 12px 0 0', display: 'block' }}
          />
        </div>

        {/* Card de login */}
        <div
          style={{
            background: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(0,212,255,0.15)',
            borderTop: 'none',
            borderRadius: '0 0 16px 16px',
            padding: '32px',
            boxShadow: glowPulse
              ? '0 0 0 1px rgba(0,212,255,0.1), 0 20px 60px rgba(0,0,0,0.6), 0 0 80px rgba(0,212,255,0.06)'
              : '0 20px 60px rgba(0,0,0,0.6)',
            transition: 'box-shadow 1.5s ease',
          }}
        >
          {/* Linha separadora decorativa */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
            <span style={{ color: 'rgba(0,212,255,0.5)', fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: 'uppercase' }}>
              Acesso ao Sistema
            </span>
            <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
          </div>

          <form onSubmit={handleSubmit(handleLogin)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* E-mail */}
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
                E-mail
              </label>
              <div style={{ position: 'relative' }}>
                <EnvelopeIcon style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'rgba(0,212,255,0.4)' }} />
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="seu@email.com"
                  {...register('email')}
                  style={{
                    width: '100%',
                    paddingLeft: 42,
                    paddingRight: 16,
                    paddingTop: 12,
                    paddingBottom: 12,
                    background: 'rgba(0,0,0,0.3)',
                    border: errors.email ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(0,212,255,0.15)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }}
                  onBlur={(e)  => { e.target.style.borderColor = errors.email ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }}
                />
              </div>
              {errors.email && (
                <p style={{ marginTop: 4, fontSize: 11, color: 'rgb(239,68,68)' }}>{errors.email.message}</p>
              )}
            </div>

            {/* Senha */}
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
                Senha
              </label>
              <div style={{ position: 'relative' }}>
                <LockClosedIcon style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'rgba(0,212,255,0.4)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  {...register('password')}
                  style={{
                    width: '100%',
                    paddingLeft: 42,
                    paddingRight: 46,
                    paddingTop: 12,
                    paddingBottom: 12,
                    background: 'rgba(0,0,0,0.3)',
                    border: errors.password ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(0,212,255,0.15)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }}
                  onBlur={(e)  => { e.target.style.borderColor = errors.password ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.3)' }}
                >
                  {showPassword
                    ? <EyeSlashIcon style={{ width: 16, height: 16 }} />
                    : <EyeIcon style={{ width: 16, height: 16 }} />
                  }
                </button>
              </div>
              {errors.password && (
                <p style={{ marginTop: 4, fontSize: 11, color: 'rgb(239,68,68)' }}>{errors.password.message}</p>
              )}
            </div>

            {/* Botão Entrar */}
            <button
              type="submit"
              disabled={busy}
              style={{
                marginTop: 8,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '14px 24px',
                background: busy
                  ? 'rgba(0,212,255,0.3)'
                  : 'linear-gradient(135deg, #00b4d8 0%, #00d4ff 50%, #00e5ff 100%)',
                border: 'none',
                borderRadius: 8,
                color: '#0a0f1e',
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: 1,
                cursor: busy ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: busy ? 'none' : '0 4px 24px rgba(0,212,255,0.35)',
              }}
              onMouseEnter={(e) => { if (!busy) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 40px rgba(0,212,255,0.6)'; }}
              onMouseLeave={(e) => { if (!busy) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 24px rgba(0,212,255,0.35)'; }}
            >
              {busy ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  ENTRAR
                  <ArrowRightIcon style={{ width: 16, height: 16 }} />
                </>
              )}
            </button>
          </form>

          {/* Link para cadastro */}
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/register')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '11px 20px',
                width: '100%',
                justifyContent: 'center',
                background: 'rgba(0,212,255,0.06)',
                border: '1px solid rgba(0,212,255,0.2)',
                borderRadius: 8,
                color: 'rgba(0,212,255,0.8)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                letterSpacing: 0.5,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,212,255,0.12)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,212,255,0.4)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,212,255,0.06)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,212,255,0.2)';
              }}
            >
              <UserPlusIcon style={{ width: 15, height: 15 }} />
              Criar nova conta
            </button>
          </div>

          {/* Rodapé do card */}
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 6 }}>Suporte e acesso</p>
            <a
              href="mailto:thiago@incluirtech.com.br"
              style={{ fontSize: 12, color: 'rgba(0,212,255,0.7)', textDecoration: 'none', letterSpacing: 0.5 }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = '#00d4ff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(0,212,255,0.7)'; }}
            >
              thiago@incluirtech.com.br
            </a>
          </div>
        </div>

        {/* Copyright */}
        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: 1 }}>
          © {new Date().getFullYear()} INCLUIRTECH · TODOS OS DIREITOS RESERVADOS
        </p>
      </div>

      {/* ── CSS Animations ── */}
      <style>{`
        @keyframes gridScroll {
          from { background-position: 0 0; }
          to   { background-position: 50px 50px; }
        }
        @keyframes orb1 {
          0%,100% { transform: translate(0,0) scale(1); }
          33%      { transform: translate(-3%,4%) scale(1.05); }
          66%      { transform: translate(2%,-2%) scale(0.97); }
        }
        @keyframes orb2 {
          0%,100% { transform: translate(0,0) scale(1); }
          40%      { transform: translate(4%,-5%) scale(1.08); }
          70%      { transform: translate(-2%,3%) scale(0.95); }
        }
        @keyframes particleUp {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          8%   { opacity: 0.8; }
          88%  { opacity: 0.5; }
          100% { transform: translateY(-130px) translateX(var(--drift)); opacity: 0; }
        }
        @keyframes logoFloat {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-5px); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input::placeholder { color: rgba(255,255,255,0.2); }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px rgba(0,0,0,0.6) inset !important;
          -webkit-text-fill-color: #fff !important;
          caret-color: #fff;
        }
      `}</style>
    </div>
  );
}
